import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  Building2,
  Check,
  CheckCircle2,
  Clock,
  Info,
  Save,
  X,
} from "lucide-react";
import { motion } from "framer-motion";
import { Link } from "@tanstack/react-router";
import SectionCard from "@/components/shared/SectionCard";
import { useSubmitStudentWorkLog, useStudentPlacements } from "@/hooks/useStudentData";
import PageHeader from "@/components/shared/PageHeader";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Field } from "@/components/forms";
import { DatePicker } from "@/components/ui/date-picker";

export const Route = createFileRoute("/student/work-logs/new")({ component: NewWorkLog });

function NewWorkLog() {
  const [submitted, setSubmitted] = useState(false);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [timeStart, setTimeStart] = useState("08:00");
  const [timeEnd, setTimeEnd] = useState("16:00");
  const [description, setDescription] = useState("");
  const [challenges, setChallenges] = useState("");
  const [placementId, setPlacementId] = useState<number | "">("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState("");
  const submitWorkLog = useSubmitStudentWorkLog();
  const { data: placements, isLoading: placementsLoading } = useStudentPlacements();

  const activePlacements = useMemo(() => placements ?? [], [placements]);

  // With a single placement there is nothing to choose, so it is preselected.
  // With several the student has to say which partner the hours are for,
  // because each partner only reviews their own.
  useEffect(() => {
    if (activePlacements.length === 1) {
      setPlacementId(activePlacements[0].id);
    }
  }, [activePlacements]);

  const selectedPlacement = activePlacements.find((p) => p.id === placementId);

  const calcHours = () => {
    if (!timeStart || !timeEnd) return 0;
    const [sh, sm] = timeStart.split(":").map(Number);
    const [eh, em] = timeEnd.split(":").map(Number);
    return Math.max(0, (eh * 60 + em - sh * 60 - sm) / 60);
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!date) newErrors.date = "Date is required";
    if (!timeStart) newErrors.timeStart = "Start time is required";
    if (!timeEnd) newErrors.timeEnd = "End time is required";
    if (activePlacements.length > 1 && placementId === "")
      newErrors.placementId = "Choose the partner these hours are for";
    if (activePlacements.length === 1 && placementId === "")
      newErrors.placementId = "Your placement could not be loaded";
    if (description.length < 50)
      newErrors.description = "Please provide a more detailed description (min 50 chars)";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    setSubmitError("");
    if (!validate()) return;
    if (placementId === "") return;

    try {
      await submitWorkLog.mutateAsync({
        placement_id: placementId,
        date,
        hours: calcHours(),
        description,
        challenge: challenges || "None reported",
      });
      setSubmitted(true);
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        "Your work log could not be submitted. Please try again.";
      setSubmitError(message);
    }
  };

  if (submitted) {
    return (
      <div className="py-16 text-center md:py-20">
        <motion.div
          initial={{ scale: 0.96, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          <div className="mx-auto mb-6 flex size-12 items-center justify-center rounded-md border border-success/30 bg-success/10 text-success">
            <CheckCircle2 className="size-6" aria-hidden />
          </div>
          <h1 className="mb-2 text-display-sm font-normal text-foreground">
            Submission successful
          </h1>
          <p className="mx-auto mb-8 max-w-md text-sm leading-6 text-body">
            Your work log has been recorded
            {selectedPlacement ? ` for ${selectedPlacement.partner_name}` : ""} and queued for
            supervisor review. You can track its status in your work log history.
          </p>
          <div className="flex flex-wrap justify-center gap-2">
            <Button asChild>
              <Link to="/student/work-logs">View history</Link>
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setSubmitted(false);
                setDescription("");
                setChallenges("");
                setDate(new Date().toISOString().slice(0, 10));
                setSubmitError("");
              }}
            >
              Add another
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }

  const placementHint = placementsLoading
    ? "Loading your placements..."
    : selectedPlacement
      ? `${selectedPlacement.hours_logged ?? 0} of ${selectedPlacement.hours_required ?? 0} hours approved so far`
      : "Your active placements could not be loaded.";

  return (
    <div className="space-y-6">
      <PageHeader
        title="Submit work log"
        subtitle="Record daily hours, practical activity, and learning outcomes for supervisor review."
        breadcrumbs={[
          { label: "Dashboard", path: "/student/dashboard" },
          { label: "Work Logs", path: "/student/work-logs" },
          { label: "New Entry" },
        ]}
        action={
          <Button variant="outline" asChild>
            <Link to="/student/work-logs">
              <ArrowLeft />
              Back to history
            </Link>
          </Button>
        }
      />

      <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <SectionCard title="Task details">
          <div className="space-y-5">
            {!placementsLoading && activePlacements.length === 0 && (
              <Alert variant="warning">
                <AlertTriangle />
                <AlertDescription className="text-foreground">
                  You do not have an active placement, so there is no partner to send these hours
                  to. Work logs need an active placement before they can be submitted.
                </AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              {activePlacements.length > 1 && (
                <p className="text-sm text-muted-foreground">
                  You are on more than one placement. Pick the partner this work was done for,
                  because that is who reviews and verifies these hours.
                </p>
              )}
              <Field
                label="Partner this work is for"
                error={errors.placementId}
                hint={placementHint}
              >
                {({ id, describedBy, invalid }) => (
                  <Select
                    value={placementId === "" ? "" : String(placementId)}
                    onValueChange={(v) => setPlacementId(Number(v))}
                    disabled={placementsLoading || activePlacements.length === 0}
                  >
                    <SelectTrigger
                      id={id}
                      aria-describedby={describedBy}
                      aria-invalid={invalid || undefined}
                      className="h-auto min-h-9 py-1.5"
                    >
                      <SelectValue
                        placeholder={activePlacements.length > 1 ? "Select partner" : "Partner"}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {activePlacements.map((p) => (
                        <SelectItem key={p.id} value={String(p.id)}>
                          <span className="flex items-center gap-2.5">
                            <Building2 className="size-4 shrink-0 text-muted-foreground" />
                            <span className="flex flex-col text-left">
                              <span className="text-sm text-foreground">{p.partner_name}</span>
                              <span className="text-caption text-muted-foreground">
                                {p.slot_title}
                                {p.location ? ` · ${p.location}` : ""}
                              </span>
                            </span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label="Date of work"
                error={errors.date}
                hint="Hours cannot be logged for a future day"
              >
                <DatePicker
                  value={date}
                  onChange={(v) => setDate(v)}
                  // The picker stops at today: hours are claimed for work already done.
                  max={new Date().toISOString().slice(0, 10)}
                />
              </Field>
              <div className="flex items-center gap-3 self-start rounded-md border border-border bg-muted/40 px-4 py-3 sm:mt-[22px]">
                <Clock className="size-4 shrink-0 text-muted-foreground" aria-hidden />
                <div>
                  <p className="eyebrow">Calculated time</p>
                  <p className="text-lg leading-7 text-foreground tabular-nums" aria-live="polite">
                    {calcHours().toFixed(1)} Hours
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Field label="Start time" error={errors.timeStart}>
                <Input type="time" value={timeStart} onChange={(e) => setTimeStart(e.target.value)} />
              </Field>
              <Field label="End time" error={errors.timeEnd}>
                <Input type="time" value={timeEnd} onChange={(e) => setTimeEnd(e.target.value)} />
              </Field>
            </div>

            <Field
              label="Activity description"
              error={errors.description}
              hint={`${description.length} characters`}
            >
              <Textarea
                rows={6}
                placeholder="What did you accomplish today? Be specific about projects, meetings, and learning outcomes."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </Field>

            <Field label="Challenges & blockers">
              <Textarea
                rows={3}
                placeholder="List any difficulties you faced or areas where you need guidance."
                value={challenges}
                onChange={(e) => setChallenges(e.target.value)}
              />
            </Field>

            <Separator />

            {submitError && (
              <Alert variant="destructive">
                <AlertCircle />
                <AlertDescription className="flex-1 text-foreground">{submitError}</AlertDescription>
                <button
                  type="button"
                  onClick={() => setSubmitError("")}
                  aria-label="Dismiss"
                  className="-my-0.5 rounded-xs p-0.5 text-muted-foreground hover:text-foreground"
                >
                  <X className="size-4" />
                </button>
              </Alert>
            )}

            <div className="flex flex-wrap gap-2">
              <Button
                onClick={handleSubmit}
                loading={submitWorkLog.isPending}
                disabled={
                  submitWorkLog.isPending ||
                  placementsLoading ||
                  activePlacements.length === 0 ||
                  placementId === ""
                }
              >
                {!submitWorkLog.isPending && <Save />}
                {submitWorkLog.isPending ? "Submitting..." : "Submit log"}
              </Button>
              <Button variant="ghost" asChild>
                <Link to="/student/work-logs">Cancel</Link>
              </Button>
            </div>
          </div>
        </SectionCard>

        <div className="flex min-w-0 flex-col gap-4">
          <SectionCard title="Submission guide">
            <ul className="space-y-3">
              {[
                "Be specific about your contributions.",
                "Mention any tools or software used.",
                "Include learning outcomes.",
                "Submit before the end of the day.",
              ].map((text, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm text-body">
                  <Check className="mt-0.5 size-4 shrink-0 text-success" aria-hidden />
                  {text}
                </li>
              ))}
            </ul>
          </SectionCard>

          <section className="rounded-md border border-border bg-card p-4 md:p-5">
            <div className="mb-2 flex items-center gap-2">
              <Info className="size-4 text-muted-foreground" aria-hidden />
              <h2 className="text-sm font-medium text-foreground">Review cycle</h2>
            </div>
            <p className="text-sm leading-6 text-muted-foreground">
              Your supervisor reviews logs every Friday afternoon. Ensure all weekly entries are
              submitted by 4:00 PM Thursday.
            </p>
          </section>

          <section className="rounded-md border border-border bg-card p-4 md:p-5">
            <h2 className="mb-2 text-sm font-medium text-foreground">What this unlocks</h2>
            <p className="text-sm leading-6 text-muted-foreground">
              Consistent approved work logs strengthen certificate eligibility, completion records,
              and recommendation readiness after verified placement completion.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
